import { useMemo, useState } from 'react';
import {
  BLOCK_CATALOG,
  BLOCK_TEMPLATES,
  parseBlockSchema,
  serializeBlockSchema,
  type ThemeBlock,
} from '@/lib/themeBlocks';

// ============================================================
// Éditeur visuel de « Blocs React » pour une page de thème.
// Au lieu d'écrire du HTML, l'auteur compose la page avec des
// blocs (hero, produits, galerie, formulaire…). Le résultat est
// stocké en JSON et rendu par de vrais composants React sur le
// site public (voir ThemeBlockRenderer).
// ============================================================

const CATEGORY_LABELS: Record<string, string> = {
  structure: 'Structure',
  contenu: 'Contenu',
  dynamique: 'Dynamique',
  marketplace: 'Marketplace',
};

const CATEGORY_ORDER = ['structure', 'contenu', 'dynamique', 'marketplace'];

interface PageBlockEditorProps {
  value: string;
  onChange: (value: string) => void;
}

function BlockPropsEditor({ block, onCommit }: { block: ThemeBlock; onCommit: (props: Record<string, unknown>) => void }) {
  const [text, setText] = useState(() => JSON.stringify(block.props || {}, null, 2));
  const [err, setErr] = useState('');

  const handleChange = (v: string) => {
    setText(v);
    try {
      const parsed = JSON.parse(v || '');
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        setErr('');
        onCommit(parsed as Record<string, unknown>);
      } else {
        setErr('Le JSON doit être un objet { … }');
      }
    } catch {
      setErr('JSON invalide');
    }
  };

  return (
    <div>
      <textarea
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        rows={4}
        spellCheck={false}
        className="w-full px-2 py-1.5 rounded-md border border-background-200/70 bg-background-50 text-[11px] font-mono leading-relaxed text-foreground-800 resize-y focus:outline-none focus:border-primary-300"
      />
      {err && <p className="text-[10px] text-red-500 mt-0.5">{err}</p>}
    </div>
  );
}

export default function PageBlockEditor({ value, onChange }: PageBlockEditorProps) {
  const [showJson, setShowJson] = useState(false);
  const [showPalette, setShowPalette] = useState(true);

  const blocks = useMemo<ThemeBlock[]>(() => parseBlockSchema(value)?.blocks || [], [value]);

  const update = (next: ThemeBlock[]) => onChange(serializeBlockSchema(next));

  const addBlock = (type: string) => {
    const entry = BLOCK_CATALOG.find((b) => b.type === type);
    update([...blocks, { type, props: { ...(entry?.defaultProps || {}) } }]);
  };

  const applyTemplate = (templateId: string) => {
    const tpl = BLOCK_TEMPLATES.find((t) => t.id === templateId);
    if (!tpl) return;
    if (blocks.length > 0 && !window.confirm('Appliquer ce modèle remplacera les blocs actuels de la page. Continuer ?')) return;
    update(tpl.blocks.map((b) => ({ type: b.type, props: { ...(b.props || {}) } })));
  };

  const moveBlock = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    [next[index], next[target]] = [next[target], next[index]];
    update(next);
  };

  const removeBlock = (index: number) => update(blocks.filter((_, i) => i !== index));

  const setBlockProps = (index: number, props: Record<string, unknown>) => {
    update(blocks.map((b, i) => (i === index ? { ...b, props } : b)));
  };

  const grouped = CATEGORY_ORDER.map((cat) => ({
    cat,
    label: CATEGORY_LABELS[cat] || cat,
    items: BLOCK_CATALOG.filter((b) => b.category === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="border border-background-200/70 rounded-md overflow-hidden">
      {/* Barre d'outils */}
      <div className="flex items-center justify-between px-3 py-2 bg-background-100 border-b border-background-200/70">
        <span className="flex items-center gap-2 text-[11px] font-semibold text-foreground-600">
          <i className="ri-layout-masonry-line text-accent-600"></i>
          Blocs React
          <span className="text-[10px] font-normal text-foreground-400">({blocks.length} bloc{blocks.length > 1 ? 's' : ''})</span>
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowPalette((v) => !v)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${showPalette ? 'bg-foreground-950 text-background-50' : 'bg-background-50 text-foreground-600 border border-background-200/70'}`}
          >
            <i className="ri-add-circle-line mr-1"></i>Palette
          </button>
          <button
            type="button"
            onClick={() => setShowJson((v) => !v)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${showJson ? 'bg-foreground-950 text-background-50' : 'bg-background-50 text-foreground-600 border border-background-200/70'}`}
          >
            <i className="ri-code-line mr-1"></i>JSON
          </button>
        </div>
      </div>

      {/* JSON brut */}
      {showJson && (
        <div className="p-3 border-b border-background-200/70 bg-background-50">
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={12}
            spellCheck={false}
            className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-[11px] font-mono leading-relaxed text-foreground-800 resize-y focus:outline-none focus:border-primary-300"
          />
        </div>
      )}

      <div className="flex flex-col lg:flex-row">
        {/* Palette de blocs */}
        {showPalette && (
          <div className="lg:w-[240px] border-b lg:border-b-0 lg:border-r border-background-200/70 bg-background-100/40 p-2 max-h-[420px] overflow-y-auto flex-shrink-0">
            {/* Modèles prêts à l'emploi */}
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wider text-accent-700 font-semibold px-2 py-1 flex items-center gap-1">
                <i className="ri-magic-line text-[11px]"></i>Modèles prêts
              </div>
              {BLOCK_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => applyTemplate(tpl.id)}
                  title={tpl.description}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-xs text-foreground-700 hover:bg-accent-50 hover:text-accent-800 transition-colors cursor-pointer mb-0.5 text-left border border-accent-200/60 bg-accent-50/50"
                >
                  <i className={`${tpl.icon} text-sm flex-shrink-0 text-accent-600`}></i>
                  <span className="truncate flex-1">{tpl.label}</span>
                  <i className="ri-add-line text-[10px] flex-shrink-0"></i>
                </button>
              ))}
            </div>
            <div className="border-t border-background-200/70 mb-2"></div>
            {grouped.map((g) => (
              <div key={g.cat} className="mb-2">
                <div className="text-[10px] uppercase tracking-wider text-foreground-400 font-semibold px-2 py-1">{g.label}</div>
                {g.items.map((b) => (
                  <button
                    key={b.type}
                    type="button"
                    onClick={() => addBlock(b.type)}
                    title={b.description}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-foreground-600 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer mb-0.5 text-left"
                  >
                    <i className={`${b.icon} text-sm flex-shrink-0`}></i>
                    <span className="truncate">{b.label}</span>
                    <i className="ri-add-line text-[10px] ml-auto flex-shrink-0"></i>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Liste des blocs */}
        <div className="flex-1 p-3 space-y-2 max-h-[520px] overflow-y-auto">
          {blocks.length === 0 ? (
            <div className="text-center py-10">
              <i className="ri-layout-masonry-line text-3xl text-foreground-300 mb-2 block"></i>
              <p className="text-xs text-foreground-500">Aucun bloc pour l&apos;instant.</p>
              <p className="text-[10px] text-foreground-400 mt-1">Clique sur « Palette » puis ajoute des blocs (Hero, Produits, Galerie…).</p>
            </div>
          ) : (
            blocks.map((block, i) => {
              const def = BLOCK_CATALOG.find((b) => b.type === block.type);
              return (
                <div key={`${block.type}-${i}`} className="border border-background-200/70 rounded-md overflow-hidden bg-background-50">
                  <div className="flex items-center gap-2 px-2.5 py-1.5 bg-background-100/60">
                    <i className={`${def?.icon || 'ri-square-line'} text-sm text-foreground-500 flex-shrink-0`}></i>
                    <span className="text-xs font-medium text-foreground-700 flex-1 truncate">{def?.label || block.type}</span>
                    <button type="button" onClick={() => moveBlock(i, -1)} disabled={i === 0} title="Monter"
                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-background-200/70 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed">
                      <i className="ri-arrow-up-s-line text-foreground-500"></i>
                    </button>
                    <button type="button" onClick={() => moveBlock(i, 1)} disabled={i === blocks.length - 1} title="Descendre"
                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-background-200/70 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed">
                      <i className="ri-arrow-down-s-line text-foreground-500"></i>
                    </button>
                    <button type="button" onClick={() => removeBlock(i)} title="Supprimer"
                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 cursor-pointer">
                      <i className="ri-delete-bin-line text-xs text-red-500"></i>
                    </button>
                  </div>
                  <div className="p-2.5">
                    <BlockPropsEditor key={JSON.stringify(block.props)} block={block} onCommit={(props) => setBlockProps(i, props)} />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}